import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  Paper,
  Avatar,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  QrCode2 as QrCodeIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchQRCodeById, assignQRCodeToUser, generateQRImageUrl } from '../qrCodeApi';
import { fetchUsers } from '../usersApi';

const AssignQRCode = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [qrCode, setQRCode] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [qrData, userData] = await Promise.all([
        fetchQRCodeById(id),
        fetchUsers()
      ]);
      setQRCode(qrData);
      setUsers(userData.filter(u => u.Role !== 'Driver'));
      setSelectedUserId(qrData.UserId || '');
    } catch (error) {
      Swal.fire(t('error'), t('loadError') || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      Swal.fire(t('error'), t('selectUserError') || 'Please select a user', 'error');
      return;
    }

    try {
      await assignQRCodeToUser(id, selectedUserId);
      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: t('qrAssigned') || 'QR code assigned successfully'
      });
      navigate('/admin/qrcodes');
    } catch (error) {
      Swal.fire(t('error'), t('assignError') || 'Assignment failed', 'error');
    }
  };

  const handleUnassign = async () => {
    try {
      await assignQRCodeToUser(id, null);
      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: t('qrUnassigned') || 'QR code unassigned'
      });
      navigate('/admin/qrcodes');
    } catch (error) {
      Swal.fire(t('error'), t('unassignError') || 'Unassign failed', 'error');
    }
  };

  if (loading || !qrCode) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <PersonAddIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                {t('assignQRCode') || 'Assign QR Code'}
              </Typography>
            </Stack>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/qrcodes')}
              sx={{ borderRadius: 50 }}
            >
              {t('back')}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        
        <CardContent>
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            {/* QR Code Preview */}
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <img
                src={generateQRImageUrl(qrCode.TrackingUrl)}
                alt={`QR-${qrCode.Code}`}
                style={{ width: '100%', maxWidth: 200, height: 'auto' }}
              />
              <Typography variant="h6" sx={{ mt: 2, fontFamily: 'monospace', fontWeight: 'bold' }}>
                {qrCode.Code}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('qrCodeID') || 'QR Code ID'}: #{qrCode.Id}
              </Typography>
            </Paper>

            {/* Current Assignment Info */}
            {qrCode.UserId && qrCode.AssignedUser && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('currentlyAssignedTo') || 'Currently assigned to'}:{' '}
                <strong>{qrCode.AssignedUser.FullName}</strong>
              </Alert>
            )}

            {/* User Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('selectUser') || 'Select User'}</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label={t('selectUser') || 'Select User'}
              >
                <MenuItem value="">
                  <em>{t('unassign') || 'Unassign'}</em>
                </MenuItem>
                {users.map(user => (
                  <MenuItem key={user.Id} value={user.Id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: '0.9rem' }}>
                        {user.FullName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{user.FullName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.Email || user.Phone}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/qrcodes')}
                sx={{ borderRadius: 50 }}
              >
                {t('cancel')}
              </Button>
              {qrCode.UserId && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleUnassign}
                  sx={{ borderRadius: 50 }}
                >
                  {t('unassign') || 'Unassign'}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleAssign}
                disabled={!selectedUserId || selectedUserId === qrCode.UserId}
                sx={{ borderRadius: 50 }}
              >
                {t('assignQRCode') || 'Assign QR Code'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AssignQRCode;
